import cx from 'classnames';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';

import { ERROR, PENDING, SUCCESS } from '../../../../../design-system/constants';
import { Replay } from '../../../../../design-system/Icons/Replay';
import { CircularLoader } from '../../../../../design-system/Loader/CircularLoader';
import { deleteAttachment } from '../../../../../resources/discussion/actions';
import { getAttachmentStatusBySendingMessageId } from '../../../../../resources/discussion/selectors';
import { Attachment as AttachmentType } from '../../../../../resources/discussion/types';
import { DossierId } from '../../../../../resources/dossier/types';
import { useAppDispatch, useAppSelector } from '../../../../../shared/redux';

import s from './NewMessageBox.css';

type Props = {
    attachment: AttachmentType;
    sendingMessageId: string;
    retryUploadAttachment: (file: AttachmentType) => void;
};

export function Attachment({
    attachment,
    sendingMessageId,
    retryUploadAttachment,
}: Props): JSX.Element {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { file, category } = attachment;

    const attachmentStatus = useAppSelector(
        getAttachmentStatusBySendingMessageId(dossierId, sendingMessageId, attachment.file.name),
    );

    const hasCategory = !!category;
    const hasError = attachmentStatus === ERROR;
    const isLoading = attachmentStatus === PENDING;
    const isLoaded = attachmentStatus === SUCCESS;
    const type = file.name.replace(/.*\./, '');

    return (
        <div
            key={file.name}
            className={cx(s.attachment, { [s.attachmentError]: hasError })}
            data-testid="nmb-attachment"
        >
            <div className={s.type}>{type}</div>
            <p>{file.name}</p>
            {hasCategory && (
                <p className={s.category} data-testid="nmb-attachment-category">
                    {formatMessage({
                        id: convertCategoryToTranslation(category),
                    })}
                </p>
            )}
            {hasError && (
                <a
                    className={cx(s.attachmentIcon, s.retryAttachment)}
                    onClick={() => retryUploadAttachment(attachment)}
                    data-testid="nmb-retry"
                >
                    <Replay color="var(--red)" />
                </a>
            )}
            {isLoading && (
                <CircularLoader className={s.loader} data-testid="nmb-attachment-loader" />
            )}
            {isLoaded && (
                <a
                    className={cx(s.attachmentIcon)}
                    onClick={() =>
                        dispatch(deleteAttachment(file.name, dossierId, sendingMessageId))
                    }
                    data-testid="nmb-delete-attachment"
                >
                    <CloseIcon sx={{ color: 'icon.primary', width: '24px', height: '24px' }} />
                </a>
            )}
        </div>
    );
}

function convertCategoryToTranslation(category: string | null) {
    switch (category) {
        case 'before_trip_quotation':
            return 'discussion.add-attachments-modal.categories.quote';
        case 'during_trip_roadbook':
            return 'discussion.add-attachments-modal.categories.roadbook';
        case 'before_trip_other':
        case 'during_trip_other':
        default:
            return 'discussion.add-attachments-modal.categories.other';
    }
}
